/**
 * FormFieldDefinition → Tabulator Column Converter
 *
 * Mirrors schemaToFormDef.ts pattern but for table columns instead of form fields.
 * Maps backend FormFieldType to Tabulator ColumnDefinition properties (formatter,
 * sorter, headerFilter, width).
 *
 * 3-Layer Column Model:
 *   Layer 1: FormSchema fields → all possible columns (this converter)
 *   Layer 2: Lens evaluation → narrows visible columns
 *   Layer 3: User localStorage → personal column preferences
 *
 * Reuses helpers from tabulatorConfig.ts for consistent appearance.
 *
 * FERP Principles:
 * - Data-driven: column definitions from backend FormSchema, not hardcoded
 * - Dumb Frontend: converter is a pure mapping function
 * - Life-safety: PII fields get masking formatters
 */

import type { FormFieldDefinition, FormSection } from '@/generated/graphql';
import { FormFieldType, ModuleTier } from '@/generated/graphql';
import type { CellComponent } from '@/types/tabulator';
import { humanizeFieldName } from './tabulatorConfig';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Extended column type with visibility metadata.
 * Matches the interface already used in TabulatorPrimitiveManager.
 */
export interface ExtendedColumn extends Record<string, unknown> {
  title: string;
  field: string;
  /** false = hidden by default, must opt-in via Columns menu */
  defaultVisible?: boolean;
  /** true = cannot be hidden (Name, Actions) */
  essential?: boolean;
  /** Column group category derived from FormSchema section title */
  category?: string;
  /**
   * Whether this field can be used as a group-by key in table views.
   * Sourced from backend FormSchema `groupable: true` flag — never hardcoded.
   * TabulatorPrimitiveManager merges these with GROUPABLE_SYSTEM_FIELDS for
   * the group-by menu.
   */
  groupable?: boolean;
}

// ============================================================================
// FORMATTERS
// ============================================================================

/** Date formatter for timestamp columns — matches existing TabulatorPrimitiveManager */
const formatTimestamp = (cell: CellComponent) => {
  const value = cell.getValue();
  if (!value) return '';
  const date = new Date(value as string);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** PII mask formatter — shows only last 4 characters */
const formatMasked = (cell: CellComponent) => {
  const value = cell.getValue() as string;
  if (!value) return '';
  if (value.length <= 4) return value;
  return `${'*'.repeat(value.length - 4)}${value.slice(-4)}`;
};

/** Truncate long text for table display */
const formatTruncated =
  (maxLen: number) => (cell: CellComponent) => {
    const value = cell.getValue() as string;
    if (!value) return '';
    return value.length > maxLen ? `${value.slice(0, maxLen)}...` : value;
  };

/** Boolean tick/cross display */
const TICK_CROSS_PARAMS = {
  tickElement:
    '<span style="color: var(--mantine-color-green-6);">&#10003;</span>',
  crossElement:
    '<span style="color: var(--mantine-color-red-6);">&#10007;</span>',
};

// ============================================================================
// STATUS / BADGE MAPS (shared with TabulatorPrimitiveManager)
// ============================================================================

export const STATUS_BADGE_MAP: Record<string, string> = {
  ACTIVE: 'low',
  PENDING: 'high',
  ARCHIVED: 'normal',
  DRAFT: 'builtin',
  COMPLETED: 'low',
  PAUSED: 'normal',
  FAILED: 'critical',
};

const SAFETY_BADGE_MAP: Record<string, string> = {
  CRITICAL: 'critical',
  HIGH: 'urgent',
  MEDIUM: 'high',
  LOW: 'low',
};

/** Badge formatter using FERP CSS classes */
const formatBadge =
  (colorMap: Record<string, string>) => (cell: CellComponent) => {
    const value = cell.getValue() as string;
    if (!value) return '';
    const cssClass = colorMap[value] ?? 'normal';
    return `<span class="ferp-tbl-badge ferp-tbl-badge--${cssClass}">${value}</span>`;
  };

/** Build headerFilter list params from SelectOption array */
function buildSelectFilterParams(
  options?: FormFieldDefinition['options'],
): Record<string, unknown> | undefined {
  if (!options || options.length === 0) return undefined;
  const values: Record<string, string> = { '': 'All' };
  for (const opt of options) {
    values[opt.value] = opt.label;
  }
  return { values };
}

// ============================================================================
// SYSTEM COLUMNS — fields on every primitive but NOT in FormSchema
// ============================================================================

/**
 * System columns that exist on all 25 primitives via the shared GraphQL type.
 * These are not "form fields" (you don't type them into a form), but they
 * ARE table columns. Prepended before FormSchema-derived columns.
 */
export const SYSTEM_COLUMNS: ExtendedColumn[] = [
  {
    title: 'Name',
    field: 'name',
    sorter: 'string',
    headerFilter: 'input',
    headerFilterPlaceholder: 'Search names...',
    essential: true,
    defaultVisible: true,
    category: 'System',
  },
  {
    title: 'Description',
    field: 'description',
    sorter: 'string',
    headerFilter: 'input',
    headerFilterPlaceholder: 'Search descriptions...',
    defaultVisible: true,
    category: 'System',
    tooltip: (_e: MouseEvent, cell: CellComponent) => {
      const value = cell.getValue() as string;
      return value || '';
    },
    formatter: (cell: CellComponent) => {
      const value = cell.getValue() as string;
      if (!value) return '<span style="color: var(--mantine-color-dimmed);">No description</span>';
      return `<span style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;width:100%;" title="${value.replace(/"/g, '&quot;')}">${value}</span>`;
    },
  },
  {
    title: 'Status',
    field: 'status',
    width: 130,
    sorter: 'string',
    headerFilter: 'list',
    headerFilterParams: {
      values: {
        '': 'All',
        ACTIVE: 'Active',
        PENDING: 'Pending',
        DRAFT: 'Draft',
        ARCHIVED: 'Archived',
        COMPLETED: 'Completed',
      },
    },
    headerFilterPlaceholder: 'All',
    defaultVisible: true,
    category: 'System',
    formatter: formatBadge(STATUS_BADGE_MAP),
  },
  {
    title: 'Active',
    field: 'active',
    width: 80,
    hozAlign: 'center',
    defaultVisible: true,
    category: 'System',
    formatter: (cell: CellComponent) => {
      const val = cell.getValue();
      // Safe boolean coercion — tickCross crashes on arrays/objects
      const bool = Array.isArray(val) ? val.length > 0 : Boolean(val);
      return bool
        ? '<span style="color: var(--mantine-color-green-6);">&#10003;</span>'
        : '<span style="color: var(--mantine-color-red-6);">&#10007;</span>';
    },
  },
  {
    title: 'Safety Level',
    field: 'safetyLevel',
    width: 140,
    sorter: 'string',
    headerFilter: 'list',
    headerFilterParams: {
      values: {
        '': 'All',
        CRITICAL: 'Critical',
        HIGH: 'High',
        MEDIUM: 'Medium',
        LOW: 'Low',
      },
    },
    headerFilterPlaceholder: 'All',
    defaultVisible: false,
    category: 'System',
    formatter: formatBadge(SAFETY_BADGE_MAP),
  },
  {
    title: 'Updated',
    field: 'updatedAt',
    width: 180,
    sorter: 'string',
    defaultVisible: true,
    category: 'System',
    formatter: formatTimestamp,
  },
  {
    title: 'Created',
    field: 'createdAt',
    width: 180,
    sorter: 'string',
    defaultVisible: true,
    category: 'System',
    formatter: formatTimestamp,
  },
  {
    title: 'Created By',
    field: 'createdBy',
    width: 130,
    sorter: 'string',
    headerFilter: 'input',
    defaultVisible: false,
    category: 'System',
  },
  {
    title: 'Version',
    field: 'version',
    width: 80,
    sorter: 'number',
    defaultVisible: false,
    category: 'System',
  },
];

/** Field names covered by SYSTEM_COLUMNS — skip if they appear in FormSchema */
const SYSTEM_FIELD_NAMES = new Set(SYSTEM_COLUMNS.map((c) => c.field));

// ============================================================================
// FIELD TYPE → TABULATOR COLUMN MAPPING
// ============================================================================

/** Field types that should be skipped in table view (not displayable in a column) */
const SKIP_FIELD_TYPES: Set<string> = new Set([
  FormFieldType.HIDDEN,
  FormFieldType.GRID,
]);

// Field NAMES that are always nested GQL objects — skip regardless of fieldType.
// These come from the InstanceFields fragment and cause "Objects are not valid
// as a React child" when Tabulator tries to render them as cell values.
const SKIP_FIELD_NAMES: Set<string> = new Set([
  'availableTransitions',
  'auditInfo',
  'formulaInstanceIds',
]);

// ============================================================================
// ROW EXPANDER COLUMN (Phase 5)
// ============================================================================

/**
 * Expander column — first column, shows ▶ arrow to expand/collapse an inline
 * preview row. The expand/collapse logic is handled by TabulatorPrimitiveManager's
 * cellClick handler for the '_expander' field.
 */
export const EXPANDER_COLUMN: ExtendedColumn = {
  title: '',
  field: '_expander',
  width: 30,
  hozAlign: 'center',
  headerSort: false,
  essential: true,
  defaultVisible: true,
  category: 'System',
  cssClass: 'ferp-cell-expander',
  formatter: () => '<span class="ferp-row-expander">&#9654;</span>',
};

// ============================================================================
// TIER → VISIBILITY
// ============================================================================

/**
 * Map minimumTier to defaultVisible/essential flags.
 *
 * - FREE tier = essential (always visible, can't hide)
 * - BASIC or no tier = default visible
 * - BASIC / PROFESSIONAL / ENTERPRISE = hidden by default (opt-in via column toggle)
 */
function tierToVisibility(tier: FormFieldDefinition['minimumTier']): {
  essential: boolean;
  defaultVisible: boolean;
} {
  if (!tier) return { essential: false, defaultVisible: false };

  switch (tier) {
    case ModuleTier.FREE:
      return { essential: true, defaultVisible: true };
    case ModuleTier.BASIC:
    case ModuleTier.PROFESSIONAL:
    case ModuleTier.ENTERPRISE:
      return { essential: false, defaultVisible: false };
    default:
      return { essential: false, defaultVisible: false };
  }
}

/**
 * Convert a single FormFieldDefinition to an ExtendedColumn for Tabulator.
 * Returns null for field types that should be skipped in table view.
 */
export function formFieldToTabulatorColumn(
  field: FormFieldDefinition,
): ExtendedColumn | null {
  // Skip non-displayable field types
  if (SKIP_FIELD_TYPES.has(field.fieldType as FormFieldType)) {
    return null;
  }

  // Skip system fields (already covered by SYSTEM_COLUMNS)
  if (SYSTEM_FIELD_NAMES.has(field.name)) {
    return null;
  }

  // Skip fields that are nested GQL objects — can't render as cell text
  if (SKIP_FIELD_NAMES.has(field.name)) {
    return null;
  }

  const { essential, defaultVisible } = tierToVisibility(field.minimumTier);

  // Base column definition with safe default formatter.
  // Catches object/array values that would crash React rendering.
  const col: ExtendedColumn = {
    title: field.label || humanizeFieldName(field.name),
    field: field.name,
    essential,
    defaultVisible,
    groupable: field.groupable ?? false,
    formatter: (cell: CellComponent) => {
      const val = cell.getValue();
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') {
        // Array: show count. Object: show type or JSON summary.
        if (Array.isArray(val)) return `[${val.length} items]`;
        return (val as Record<string, unknown>).__typename as string ?? JSON.stringify(val).slice(0, 50);
      }
      return String(val);
    },
  };

  // Apply order if provided
  if (field.order != null) {
    col._order = field.order;
  }

  // PII masking overrides formatter
  if (field.maskOnDisplay) {
    col.formatter = formatMasked;
    col.headerFilter = false;
    col.width = 120;
    return col;
  }

  // Map fieldType to Tabulator column properties
  switch (field.fieldType) {
    case FormFieldType.TEXT:
    case FormFieldType.EMAIL:
    case FormFieldType.PHONE:
    case FormFieldType.MRN:
    case FormFieldType.NPI:
    case FormFieldType.READONLY:
    case FormFieldType.COMPUTED:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.headerFilterPlaceholder = `Filter ${(field.label || field.name).toLowerCase()}...`;
      break;

    case FormFieldType.TEXTAREA:
    case FormFieldType.RICH_TEXT:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = formatTruncated(80);
      col.width = 250;
      break;

    case FormFieldType.NUMBER:
    case FormFieldType.RATING:
      col.sorter = 'number';
      col.headerFilter = 'number';
      col.hozAlign = 'right';
      col.width = 100;
      break;

    case FormFieldType.CURRENCY:
      col.sorter = 'number';
      col.headerFilter = 'number';
      col.hozAlign = 'right';
      col.formatter = 'money';
      col.formatterParams = { symbol: '$', precision: 2 };
      col.width = 120;
      break;

    case FormFieldType.DATE:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = formatTimestamp;
      col.width = 140;
      break;

    case FormFieldType.DATETIME:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = formatTimestamp;
      col.width = 160;
      break;

    case FormFieldType.TIME:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.width = 100;
      break;

    case FormFieldType.BOOLEAN:
    case FormFieldType.TOGGLE:
      col.formatter = 'tickCross';
      col.formatterParams = TICK_CROSS_PARAMS;
      col.headerFilter = 'tickCross';
      col.hozAlign = 'center';
      col.width = 80;
      break;

    case FormFieldType.SELECT:
      col.sorter = 'string';
      if (field.options && field.options.length > 0) {
        col.headerFilter = 'list';
        col.headerFilterParams = buildSelectFilterParams(field.options);
        col.headerFilterPlaceholder = 'All';
      } else {
        col.headerFilter = 'input';
      }
      col.width = 130;
      break;

    case FormFieldType.MULTISELECT:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = (cell: CellComponent) => {
        const value = cell.getValue();
        if (!value) return '';
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
      };
      col.width = 180;
      break;

    case FormFieldType.REFERENCE:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = formatTruncated(12);
      col.width = 140;
      break;

    case FormFieldType.SSN:
      col.formatter = formatMasked;
      col.headerFilter = false;
      col.width = 120;
      break;

    case FormFieldType.FILE:
      col.sorter = 'string';
      col.headerFilter = false;
      col.width = 120;
      break;

    case FormFieldType.ADDRESS:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = formatTruncated(40);
      col.width = 250;
      break;

    case FormFieldType.SIGNATURE:
      col.formatter = (cell: CellComponent) => {
        const value = cell.getValue();
        return value
          ? '<span class="ferp-tbl-badge ferp-tbl-badge--low">Signed</span>'
          : '<span class="ferp-tbl-badge ferp-tbl-badge--normal">Unsigned</span>';
      };
      col.hozAlign = 'center';
      col.width = 100;
      break;

    case FormFieldType.JSON:
    case FormFieldType.CODE:
    case FormFieldType.COMPOSITE:
      col.formatter = formatTruncated(60);
      col.headerFilter = false;
      col.width = 200;
      break;

    case FormFieldType.HIERARCHICAL_SELECT:
    case FormFieldType.MASTER_DETAIL:
      col.sorter = 'string';
      col.headerFilter = 'input';
      col.formatter = (cell: CellComponent) => {
        const value = cell.getValue();
        if (!value) return '';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      };
      col.width = 180;
      break;

    // IMAGE field type — available after codegen picks up the backend IMAGE variant.
    // Uses string literal cast to avoid compile error before codegen runs.
    case 'IMAGE' as FormFieldType:
      col.formatter = (cell: CellComponent) => {
        const url = cell.getValue() as string | null;
        if (!url) return '<span style="color:var(--mantine-color-dimmed)">—</span>';
        return `<img src="${url}" alt="" style="width:36px;height:36px;border-radius:50%;object-fit:cover" loading="lazy" />`;
      };
      col.width = 60;
      col.hozAlign = 'center';
      col.headerSort = false;
      break;

    default:
      // Unknown field type — render as text
      col.sorter = 'string';
      col.headerFilter = 'input';
      break;
  }

  // Apply explicit width from FormSchema if provided (overrides type default)
  if (field.width) {
    const parsed = Number.parseInt(field.width, 10);
    if (!Number.isNaN(parsed)) {
      col.width = parsed;
    }
  }

  return col;
}

// ============================================================================
// BATCH CONVERTER
// ============================================================================

/**
 * Convert all FormSchema fields to a sorted array of Tabulator ExtendedColumns.
 *
 * Returns SYSTEM_COLUMNS (name, status, active, etc.) prepended, then
 * FormSchema-derived columns sorted by `order` field.
 *
 * Fields that overlap with SYSTEM_COLUMNS (e.g., "name", "status") are
 * deduplicated — the system column wins since it has curated formatters.
 *
 * When `sections` is provided (from FormSchema layout), each column's `category`
 * is set to the section title that contains its field name. Fields not in any
 * section get category "Other". System columns always get "System".
 */
export function formFieldsToTabulatorColumns(
  fields: FormFieldDefinition[],
  sections?: FormSection[],
): ExtendedColumn[] {
  // Build field → section title lookup from backend sections
  const fieldToSection = new Map<string, string>();
  if (sections && sections.length > 0) {
    for (const section of sections) {
      for (const fieldName of section.fields) {
        fieldToSection.set(fieldName, section.title);
      }
    }
  }

  // Convert FormSchema fields, filtering nulls and system-field duplicates
  const schemaColumns: ExtendedColumn[] = [];
  for (const field of fields) {
    const col = formFieldToTabulatorColumn(field);
    if (col) {
      // Assign category from section, or "Other" if field not in any section
      col.category = fieldToSection.get(col.field) ?? 'Other';
      schemaColumns.push(col);
    }
  }

  // Sort by order field (lower = earlier), then by field name for stability
  schemaColumns.sort((a, b) => {
    const orderA = (a._order as number) ?? 999;
    const orderB = (b._order as number) ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.field.localeCompare(b.field);
  });

  // Strip internal _order metadata before returning
  for (const col of schemaColumns) {
    delete col._order;
  }

  // System columns first (category: 'System'), then schema-derived columns
  return [...SYSTEM_COLUMNS, ...schemaColumns];
}

// ============================================================================
// VISUAL FORMATTER MAP (Phase 6-8)
// ============================================================================

import {
  sparklineFormatter,
  heatmapFormatter,
  avatarStackFormatter,
  createdBySourceFormatter,
  jurisdictionFormatter,
  violationOutcomeFormatter,
} from './tabulatorFormatters';

/**
 * Map of field names → visual cell formatters.
 * TabulatorPrimitiveManager applies these to matching columns after building
 * the column array (schema-driven or legacy). This allows specific fields to
 * opt into rich visual rendering (sparklines, heatmaps, avatar stacks) without
 * changing the FormSchema or legacy column definitions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const VISUAL_FORMATTER_MAP: Record<string, (cell: any) => string> = {
  // Sparkline: array of numbers → tiny SVG polyline
  recentActivity: sparklineFormatter,
  activityHistory: sparklineFormatter,
  trendData: sparklineFormatter,

  // Heatmap: numeric value → color-graded cell
  workload: heatmapFormatter(0, 100),
  overdueCount: heatmapFormatter(0, 10, [
    'var(--mantine-color-green-1)',
    'var(--mantine-color-red-2)',
  ]),
  riskScore: heatmapFormatter(0, 100, [
    'var(--mantine-color-green-1)',
    'var(--mantine-color-red-2)',
  ]),
  // NOTE: completionRate is T3 QAPI-protected data (DRR-114).
  // Only renderable in /qapi/ pages — not in operational VISUAL_FORMATTER_MAP.

  // Avatar stack: string[] of names → overlapping circles
  teamMembers: avatarStackFormatter(3),
  assignedActors: avatarStackFormatter(3),
  participants: avatarStackFormatter(4),

  // DRR-130: Task creation pathway badge (RuleEngine / WorkflowOperation / TenantDecision)
  createdBySource: createdBySourceFormatter,

  // DRR-117: Mandate jurisdiction colored dot with override indicator
  mandateJurisdiction: jurisdictionFormatter,

  // DRR-130: Violation check outcome badge (warn / deferred)
  violationOutcome: violationOutcomeFormatter,
};
